// The Delivery home's API client (apps/server/src/pacing.mjs).
import type { Fetch } from '../work/workApi';
import { ObjectsApiError } from '../objects/objectsApi';

export interface PacingPeriod {
  period: 'week' | 'month' | 'quarter' | 'year';
  starts: string;
  ends: string;
  total: number;
  elapsed: number;
  remaining: number;
  pct_elapsed: number | null;
}

/** A date of the month: business day or not, and its holiday. */
export interface PacingDay {
  date: string;
  business: boolean;
  holiday: string | null;
}

/** One of my projects, or a deal not yet made a project, in a month. */
export interface PacingUnit {
  key: string;
  project_id: string | null;
  opportunity_id: string | null;
  code: string | null;
  name: string;
  account_name: string | null;
  is_deal: boolean;
  confirmed_hours: number;
  assigned_hours: number;
  forecast_hours: number;
  billable_hours: number;
  logged_hours: number;
}

export interface PacingMonth {
  month: string;
  days: PacingDay[];
  units: PacingUnit[];
  /** My billable hours by project key and date. */
  daily: { key: string; date: string; hours: number }[];
}

export interface PacingTeamRow {
  key: string;
  app_user_id: string;
  name: string;
  has_photo: boolean;
  forecast_hours: number;
  billable_hours: number;
}

export interface CapacityMonthRow {
  month: string;
  business_days: number;
  weekdays: number;
  /** The month's capacity: generated, or edited when `edited`. */
  capacity: number;
  /** What it is generated as: standing × business days ÷ weekdays, to the nearest 5 h. */
  generated_capacity: number;
  edited: boolean;
  assigned: number;
}

export interface AheadRow extends Pick<PacingUnit, 'key' | 'project_id' | 'opportunity_id' | 'code' | 'name' | 'account_name' | 'is_deal'> {
  month: string;
  hours: number;
}

export interface PacingHome {
  today: string;
  me: { id: string; name: string; has_photo: boolean; standing_capacity: number | null } | null;
  error?: string;
  periods: PacingPeriod[];
  holidays: { date: string; name: string }[];
  current: PacingMonth;
  team: PacingTeamRow[];
  months: CapacityMonthRow[];
  ahead: AheadRow[];
  range: { first: string; last: string };
}

async function json<T>(f: Fetch, path: string, fallback: string, init?: RequestInit): Promise<T> {
  const response = await f(path, init);
  if (!response.ok) {
    let message = fallback;
    try {
      const body = (await response.json()) as { error?: unknown };
      if (typeof body.error === 'string') message = body.error;
    } catch {
      // Not JSON: keep the fallback.
    }
    throw new ObjectsApiError(response.status, message);
  }
  return (await response.json()) as T;
}

export const fetchPacing = (f: Fetch) => json<PacingHome>(f, '/api/work/pacing', 'Could not load your month.');

export const fetchPacingMonth = (f: Fetch, month: string) =>
  json<PacingMonth>(f, `/api/work/pacing/month?month=${encodeURIComponent(month)}`, 'Could not load that month.');

export const saveCapacity = (f: Fetch, month: string, hours: number | null) =>
  json<{ months: CapacityMonthRow[] }>(f, '/api/work/capacity', 'Could not save your capacity.', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ month, hours }),
  });
